import { h } from 'preact';
import { useMemo } from 'preact/hooks';
import { useNewStore, usePlayButton, usePlayerContext, useText } from '../../../../common/hooks';
import { Button } from '../../common';
import PlaybackWrapper from './PlaybackWrapper';

import {
  USER_CLICK,
  USER_FAST_FORWARD,
  USER_FAST_REWIND,
  USER_NEXT,
  USER_TIMESHIFTING_BACK_TO_LIVE,
  USER_TIMESHIFTING_BEGIN
} from '../../../../../types';
import { getAdjacentPlaylistMedias } from '../../../../../utils';
import { BUTTON_LAYER } from '../../../../common/constants';
import { REWIND_BUTTON_PATH, TIMESHIFTING_BEGIN } from '../../../../common/theme/svg';
import { mapTitleMetadata } from '../../../../common/utils/tools';
import { CockpitBubble } from '../../common/cockpitBubble';
import { PLAYBACK_MATCH_STATE, TIMESHIFTING_TYPE_MAP } from './const';

function PlaybackControl({ centered, hidden, mobile }) {
  const player = usePlayerContext();
  const { showCountdown, isAd } = useNewStore('ad', player);
  const { next, isExtraSmallScreen } = useNewStore('ui', player);
  const {
    id,
    isLive,
    isDVR,
    isStartOverEnabled,
    timeshifting: {
      type: timeshifting,
      isAbleToStartOver: isAbleToStartOverTS,
      startOverTimeshifting,
      canGoBackToLive
    },
    title,
    preTitle,
    additionalTitle
  } = useNewStore('media', player);
  const { playlist, playlistIdsAsString } = useNewStore('playlist', player);

  const playlistEnabled = playlist.length > 0;
  const previous = !isLive && playlistEnabled && playlist[0].src !== id;
  const showNextPlaylistBtn = !isLive && playlistEnabled && playlist[playlist.length - 1].src !== id;

  const playButton = usePlayButton(player);

  const previousBtnLabel = useText('PREVIOUS_BUTTON_LABEL');
  const nextBtnLabel = useText('NEXT_BUTTON_LABEL');

  const { previousMedia, nextMedia, previousIndex, nextIndex } = useMemo(
    () => getAdjacentPlaylistMedias(playlist, id),
    [id, playlistIdsAsString]
  );

  const hiddenPlusOrMinus10 = (isLive && !timeshifting) || ((isAd || showCountdown) && !startOverTimeshifting);
  const hiddenPrevious = isAd || showCountdown || !previous || !previousMedia;
  const hiddenStartOver = (
    !(isLive && (timeshifting ? isAbleToStartOverTS : isStartOverEnabled))
    || ((isAd || showCountdown) && !startOverTimeshifting) || (isDVR && !timeshifting)
  );
  return (
    <PlaybackWrapper centered={centered} hidden={hidden}>
      <CockpitBubble
        /* Shrinks the pill and pushes it rightwards toward the center */
        style={{ justifySelf: 'end' }}
        hidden={isExtraSmallScreen}
      >
        <Button /* PREVIOUS BUTTON */
          enableBackground
          label={previousBtnLabel}
          ariaLabel={previousBtnLabel}
          ariaHidden="false"
          hidden={hiddenPrevious}
          onClick={() => {
            player.userEvents$.next({
              action: USER_NEXT,
              source: USER_CLICK,
              value: { from: 'previous' }
            });

            player.load({
              ...previousMedia,
              config: {
                ...previousMedia.config,
                diffusion: { mode: 'tunnel', position: previousIndex + 1, length: playlist.length },
                tracking: {
                  pageProvenance: '',
                  zoneProvenance: '',
                  positionVignette: '',
                  playProvenance: 'tunnel_previous'
                },
                ...(previousIndex >= 0
                  ? {
                    startTimecode: 0,
                    ...mapTitleMetadata(playlist[previousIndex], { title, preTitle, additionalTitle })
                  } : {})
              }
            });
          }}
          mobileFriendly={mobile}
          xs={!centered}
          name="btn-next"
          disableFocus={false}
          type="icon"
          icon="previous"
          first={!centered}
        />
        <Button /* START OVER BUTTON */
          enableBackground
          label="Revenir au début"
          ariaLabel="Revenir au début"
          ariaHidden="false"
          hidden={hiddenStartOver}
          onClick={() => {
            player.startOver(TIMESHIFTING_TYPE_MAP[timeshifting]);
            player.userEvents$.next({
              action: USER_TIMESHIFTING_BEGIN,
              source: USER_CLICK
            });
          }}
          type="animated"
          mobileFriendly={mobile}
          svg={TIMESHIFTING_BEGIN}
          xs={!centered}
          transition={{
            duration: 350,
            max: -360,
            apply: (val) => ({
              transformOrigin: '52.2% 50.2%',
              transform: `rotate(${val}deg)`
            })
          }}
          name="btn-start-over"
          disableFocus={false}
          icon="back-to-start"
          first={!centered}
        />
      </CockpitBubble>

      <CockpitBubble
        /* Forces this element to always sit in the 2nd track (the 'auto' track) */
        style={{ gridColumn: 2 }}
      >
        <Button /* REWIND BUTTON */
          enableBackground
          type="feedback"
          icon="rewind"
          label="Reculer"
          ariaLabel="Reculer de 10 secondes"
          ariaHidden="false"
          hidden={hiddenPlusOrMinus10}
          onClick={() => {
            player.userEvents$.next({ action: USER_FAST_REWIND, source: USER_CLICK });
            player.rewind();
          }}
          mobileFriendly={mobile}
          svg={REWIND_BUTTON_PATH}
          svgFeedback="text10"
          fnFeedback={(val) => `-${10 * val}`}
          transition={{ max: -45, apply: (val) => ({ transform: `rotate(${val}deg)` }) }}
          xs={!centered}
          name="btn-rewind"
          disableFocus={false}
          first={hiddenStartOver && hiddenPrevious}
        />

        <Button /* PLAY BUTTON */
          enableBackground
          key="main-button"
          label={PLAYBACK_MATCH_STATE[playButton].label}
          ariaLabel={PLAYBACK_MATCH_STATE[playButton].label.replace(/ \(k\)/, ' raccourci touche k')}
          ariaHidden="false"
          type={['PLAY', 'PAUSE'].includes(playButton) ? 'icon' : 'default'}
          icon={playButton.toLowerCase()}
          toggle={playButton === 'PAUSE'}
          onClick={() => {
            if (PLAYBACK_MATCH_STATE[playButton].userEvent) {
              player.userEvents$.next({ action: PLAYBACK_MATCH_STATE[playButton].userEvent, source: USER_CLICK });
            }
            player[PLAYBACK_MATCH_STATE[playButton].fn]({ userGesture: true });
          }}
          svg={PLAYBACK_MATCH_STATE[playButton].svg}
          mobileFriendly={mobile}
          xs={!centered}
          xl
          name="btn-play"
          disableFocus={false}
        />

        <Button /* FORWARD BUTTON */
          enableBackground
          type="feedback"
          icon="forward"
          label="Avancer"
          ariaLabel="Avancer de 10 secondes"
          ariaHidden="false"
          hidden={hiddenPlusOrMinus10}
          onClick={() => {
            player.userEvents$.next({ action: USER_FAST_FORWARD, source: USER_CLICK });
            player.forward();
          }}
          mobileFriendly={mobile}
          svgFeedback="text10"
          fnFeedback={(val) => `+${10 * val}`}
          transition={{ max: 45, apply: (val) => ({ transform: `rotate(${val}deg)` }) }}
          xs={!centered}
          name="btn-forward"
          disableFocus={false}
        />
      </CockpitBubble>

      <CockpitBubble
        /* Shrinks the pill and pushes it leftwards toward the center */
        style={{ justifySelf: 'start' }}
        hidden={isExtraSmallScreen}
      >
        <Button /* BACK TO LIVE BUTTON */
          enableBackground
          label="Revenir au direct"
          ariaLabel="Revenir au direct"
          ariaHidden="false"
          // on small screen panel tooltip issue bellow the button on hover
          zIndex={BUTTON_LAYER}
          hidden={(timeshifting ? !canGoBackToLive : isLive)
            || ((isAd || showCountdown))
            || (!isDVR && !timeshifting)}
          onClick={() => {
            player.backToLive(TIMESHIFTING_TYPE_MAP[timeshifting]);
            player.userEvents$.next({
              action: USER_TIMESHIFTING_BACK_TO_LIVE,
              source: USER_CLICK
            });
          }}
          mobileFriendly={mobile}
          type="icon"
          icon="back-to-live"
          xs={!centered}
          name="btn-back-to-live"
          disableFocus={false}
        />

        <Button /* NEXT BUTTON */
          enableBackground
          label={nextBtnLabel}
          ariaLabel={nextBtnLabel}
          ariaHidden="false"
          // on small screen panel tooltip issue bellow the button on hover
          zIndex={BUTTON_LAYER}
          hidden={isDVR || isAd || showCountdown || (playlistEnabled ? (!showNextPlaylistBtn || !nextMedia) : !next)}
          onClick={() => {
            player.userEvents$.next({
              action: USER_NEXT,
              source: USER_CLICK,
              value: { from: 'next' }
            });
            if (nextMedia) {
              player.load({
                ...nextMedia,
                config: {
                  ...nextMedia.config,
                  diffusion: { mode: 'tunnel', position: nextIndex + 1, length: playlist.length },
                  tracking: {
                    pageProvenance: '',
                    zoneProvenance: '',
                    positionVignette: '',
                    playProvenance: 'tunnel_next'
                  },
                  ...(nextIndex >= 0
                    ? {
                      ...mapTitleMetadata(playlist[nextIndex], { title, preTitle, additionalTitle }),
                      startTimecode: 0
                    }
                    : {})

                }
              });
            } else {
              player.next('button');
            }
          }}
          mobileFriendly={mobile}
          xs={!centered}
          name="btn-next"
          disableFocus={false}
          type="icon"
          icon="next"
        />

      </CockpitBubble>
    </PlaybackWrapper>
  );
}

export default PlaybackControl;
